import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@mantine/core";

import { buildPeriodReport } from "@prostocheck/data-web";

import { PeriodDays } from "../period-days";
import { PeriodTotals } from "../period-totals";
import { IPeriodReportModalProps } from "./period-report-modal.models";

/**
 * The period's totals, aggregated from the Z-reports the register page has
 * already loaded for the same filter range — no separate fetch, no separate
 * date range. `buildPeriodReport` is pure, so this is just arithmetic over
 * data already on screen.
 */
export const PeriodReportModal = ({
  isOpen,
  reports,
  range,
  cashRegisterFn,
  onClose,
}: IPeriodReportModalProps) => {
  const { t } = useTranslation();
  const report = useMemo(
    () =>
      isOpen
        ? buildPeriodReport({ cashRegisterFn, from: range.from, to: range.to, reports })
        : null,
    [isOpen, cashRegisterFn, range.from, range.to, reports],
  );

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size="xl"
      title={t("register.periodReport")}
      closeButtonProps={{ "aria-label": t("common:close") }}
    >
      {report && (
        // A single wrapping element, not a fragment: Mantine's `Modal.Body`
        // zeroes its own top padding whenever it has more than one direct
        // child, which clipped the summary cards' shadow/ring against the
        // sticky header above.
        <div>
          <PeriodTotals report={report} />
          <PeriodDays report={report} />
        </div>
      )}
    </Modal>
  );
};
